/**
 * @author Georgegriff@ (George Griffiths)
 * License Apache-2.0
 */
/**
 * Finds first matching elements on the page that may be in a shadow root using a complex selector of n-depth
 *
 * Don't have to specify all shadow roots to button, tree is traversed to find the correct element
 *
 * Example querySelectorAllDeep('downloads-item:nth-child(4) #remove');
 *
 * Example should work on chrome://downloads outputting the remove button inside of a download card component
 *
 * Example find first active download link element querySelectorDeep('#downloads-list .is-active a[href^="https://"]');
 *
 * Another example querySelectorAllDeep('#downloads-list div#title-area + a');
 */
function splitByCharacterUnlessQuoted(selector, character) {
    return selector.match(/\\?.|^$/g).reduce((p, c) => {
        if (c === '"' && !p.sQuote) {
            p.quote ^= 1;
            p.a[p.a.length - 1] += c;
        }
        else if (c === "'" && !p.quote) {
            p.sQuote ^= 1;
            p.a[p.a.length - 1] += c;
        }
        else if (!p.quote && !p.sQuote && c === character) {
            p.a.push('');
        }
        else {
            p.a[p.a.length - 1] += c;
        }
        return p;
    }, { a: [''] }).a;
}
/**
 * Finds all elements on the page, inclusive of those within shadow roots.
 * @param {string=} selector Simple selector to filter the elements by. e.g. 'a', 'div.main'
 * @return {!Array<string>} List of anchor hrefs.
 * @author ebidel@ (Eric Bidelman)
 * License Apache-2.0
 */
function collectAllElementsDeep(root, selector = null) {
    const allElements = [];
    const findAllElements = function (nodes) {
        const nodeArrray = Array.from(nodes);
        for (const el of nodeArrray) {
            allElements.push(el);
            // If the element has a shadow root, dig deeper.
            if (el.shadowRoot) {
                findAllElements(el.shadowRoot.querySelectorAll('*'));
            }
        }
    };
    if (root.shadowRoot) {
        findAllElements(root.shadowRoot.querySelectorAll('*'));
    }
    findAllElements(root.querySelectorAll('*'));
    return selector
        ? allElements.filter((el) => el.matches(selector))
        : allElements;
}
/**
 * Checks if the node is a document node or not.
 * @param {Node} node
 * @returns {node is Document | DocumentFragment}
 */
function isDocumentNode(node) {
    return (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE ||
        node.nodeType === Node.DOCUMENT_NODE);
}
function findParentOrHost(element, root) {
    const { parentNode } = element;
    if (parentNode && parentNode.host && parentNode.nodeType === 11) {
        return parentNode.host;
    }
    else if (parentNode === root) {
        return null;
    }
    return parentNode;
}
function findMatchingElement(splitSelector, possibleElementsIndex, root) {
    return (element) => {
        let position = possibleElementsIndex;
        let parent = element;
        let foundElement = false;
        while (parent && !isDocumentNode(parent)) {
            const foundMatch = parent.matches(splitSelector[position]);
            if (foundMatch && position === 0) {
                foundElement = true;
                break;
            }
            if (foundMatch) {
                position--;
            }
            parent = findParentOrHost(parent, root);
        }
        return foundElement;
    };
}
function _querySelectorDeep(selector, findMany, root) {
    const lightElement = root?.querySelector(selector);
    if (document.head.createShadowRoot || document.head.attachShadow) {
        // no need to do any special if selector matches something specific in light-dom
        if (!findMany && lightElement) {
            return lightElement;
        }
        // split on commas because those are a logical divide in the operation
        const selectionsToMake = splitByCharacterUnlessQuoted(selector, ',');
        return selectionsToMake.reduce((acc, minimalSelector) => {
            let _acc = acc;
            // if not finding many just reduce the first match
            if (!findMany && _acc) {
                return _acc;
            }
            // do best to support complex selectors and split the query
            const splitSelector = splitByCharacterUnlessQuoted(minimalSelector
                // remove white space at start of selector
                .replace(/^\s+/g, '')
                .replace(/\s*([>+~]+)\s*/g, '$1'), ' ')
                // filter out entry white selectors
                .filter((entry) => !!entry);
            const possibleElementsIndex = splitSelector.length - 1;
            const possibleElements = collectAllElementsDeep(root, splitSelector[possibleElementsIndex]);
            const findElements = findMatchingElement(splitSelector, possibleElementsIndex, root);
            if (findMany) {
                _acc = _acc.concat(possibleElements.filter(findElements));
                return _acc;
            }
            _acc = possibleElements.find(findElements);
            return _acc || null;
        }, findMany ? [] : null);
    }
    if (!findMany) {
        return lightElement;
    }
    return root?.querySelectorAll(selector);
}
function querySelectorAllDeep(selector, root = document) {
    return _querySelectorDeep(selector, true, root);
}
function querySelectorDeep(selector, root = document) {
    return _querySelectorDeep(selector, false, root);
}

export { querySelectorDeep as a, querySelectorAllDeep as q };
